<template>
  <div class="bg-white">
    <CouponModal />
    <!-- HERO -->
    <section id="mainbanner" class="relative bg-center bg-no-repeat bg-cover banner-img">
      <div class="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/20"></div>

      <div class="relative max-w-7xl mx-auto px-5 md:px-8 lg:px-10 py-20 md:py-24">
        <div class="space-y-3">
          <h2 class="uppercase text-white font-semibold text-[14px] md:text-[16px] tracking-[0.18em]">
            Premium Services Tailored for You.
          </h2>
          <h1 class="text-white leading-tight">
            <div class="text-[26px] md:text-[40px] font-light">International Ground Transportation & Chauffeur Services</div>
            <div class="text-[26px] md:text-[40px] font-light">Global Online Reservations</div>
            <div class="text-[18px] md:text-[24px] font-light text-white/90 mt-2">Serving Europe, GCC, USA, and Asia</div>
          </h1>
        </div>

        <!-- Glass search card -->
        <div class="mt-8 md:mt-10">
          <div class="backdrop-blur-md bg-white/15 border border-white/20 rounded-2xl p-4 md:p-5 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.5)]">
            <!-- Mobile-only service chips -->
            <nav class="md:hidden mb-3 -mt-1">
              <div class="relative">
                <!-- edge fades -->
                <span class="pointer-events-none absolute left-0 top-0 h-full w-6 bg-gradient-to-r from-black/30 to-transparent rounded-l-2xl"></span>
                <span class="pointer-events-none absolute right-0 top-0 h-full w-6 bg-gradient-to-l from-black/30 to-transparent rounded-r-2xl"></span>

                <ul
                  class="flex gap-2 overflow-x-auto no-scrollbar snap-x snap-mandatory px-1 py-1"
                  role="tablist"
                  aria-label="Choose service"
                >
                  <li v-for="s in services" :key="s.type" class="snap-start shrink-0">
                    <button
                      type="button"
                      class="px-4 py-2 rounded-full text-[13px] font-medium border transition whitespace-nowrap"
                      :class="activeType === s.type
                              ? 'bg-green-700 text-white border-green-700'
                              : 'bg-white/95 text-slate-800 border-slate-200'"
                      @click="handleServiceSelect(s.type)"
                    >
                      {{ s.label }}
                    </button>
                  </li>
                </ul>
              </div>
              <p class="mt-2 text-center text-white/80 text-[11px]">
                Pick a service, then fill the form below.
              </p>
            </nav>

            <!-- SEARCH -->
           <Search
  :key="activeType"
  layout="simple"
  :data="search"
  :type="mappedType"    
  :showMore="false"     
/>
          </div>
        </div>
      </div>
    </section>

    <!-- INTRO STRIP -->
    <section class="max-w-7xl mx-auto px-5 md:px-8 lg:px-10 mt-16 md:mt-20 pb-10">
      <div class="grid md:grid-cols-3 gap-10 md:gap-8 items-start">
        <h2 class="md:col-span-1 font-extrabold text-[#0B6A1F] text-[28px] md:text-[44px] leading-[1.15]">
          From Sedans to Luxury.
        </h2>

        <div class="space-y-3">
          <div class="text-slate-500 text-[18px] md:text-[22px]">Expect the Best</div>
          <p class="text-slate-600 text-[16px] leading-relaxed">
            Looking for reliable global ground transportation? Whether you need a standard vehicle, a spacious people carrier for families and groups, or a luxurious SUV, we have got you covered. Our professional drivers ensure a safe and timely journey to your destination, so you can relax and enjoy the ride.
          </p>
        </div>

        <div class="space-y-3">
          <div class="text-slate-500 text-[18px] md:text-[22px]">Travel in Comfort</div>
          <p class="text-slate-600 text-[16px] leading-relaxed">
            At Vehitra World, we offer a fleet of the latest makes and models, ensuring comfort and style. Our multilingual, experienced chauffeurs are trained in both courtesy and safety, delivering a smooth and secure journey across all our global destinations.
          </p>
        </div>
      </div>

      <!-- side-by-side images -->
      <div class="grid md:grid-cols-2 gap-6 mt-10 md:mt-14">
        <img src="/assets/6.jpeg" alt="Global Professional Chauffeur Service" class="rounded-2xl shadow aspect-[16/10] object-cover w-full" />
        <img src="/assets/9.jpg" alt="Premium Fleet for International Ground Transportation" class="rounded-2xl shadow aspect-[16/10] object-cover w-full md:mt-6" />
      </div>

      <!-- quote -->
      <div class="flex flex-col items-center mt-14 md:mt-16">
        <font-awesome-icon size="xl" icon="fa-solid fa-quote-right" class="text-[#0B6A1F]" />
        <p class="mt-4 text-center text-slate-600 text-[18px] md:text-[22px] leading-relaxed">
          I invite you to try our service and I personally guarantee<br class="hidden md:block" />
          you will have a fully satisfying experience.
        </p>
        <div class="text-slate-500 uppercase mt-6 tracking-wider text-[12px]">Ghazi Hamadeh — Chairman &amp; CEO</div>
      </div>
    </section>

    <!-- WHAT WE OFFER -->
    <section class="bg-slate-50 py-16 md:py-20">
      <div class="max-w-7xl mx-auto px-5 md:px-8 lg:px-10">
        <h2 class="text-center uppercase text-[#0B6A1F] font-semibold text-[14px] tracking-[0.18em]">What we offer</h2>
        <div class="text-center text-slate-700 text-[22px] md:text-[34px] font-light mt-1">See What We Can Do For You</div>

        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-10 md:mt-12">
          <!-- Big: Airport -->
          <a href="#mainbanner"
   @click.prevent="selectSearchAndScroll('airport')"
   class="relative col-span-2 md:col-span-2 rounded-2xl overflow-hidden group">
            <img src="/assets/8.jpeg" alt="Reliable Airport Transfer Worldwide" class="w-full h-full object-cover aspect-[16/10]" />
            <div class="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent"></div>
            <div class="absolute bottom-5 left-5">
              <div class="h-[2px] w-8 bg-white mb-3 group-hover:w-16 transition-all duration-300"></div>
              <span class="text-white text-[18px] md:text-[22px] font-bold uppercase">Airport Transfer</span>
            </div>
          </a>

          <!-- Big: Chauffeur -->
          <a href="#mainbanner"
    @click.prevent="selectSearchAndScroll('hourly')"
    class="relative col-span-2 md:col-span-2 rounded-2xl overflow-hidden group">
            <img src="/assets/7.jpeg" alt="Luxury Full Day Chauffeur Driver" class="w-full h-full object-cover aspect-[16/10]" />
            <div class="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent"></div>
            <div class="absolute bottom-5 left-5">
              <div class="h-[2px] w-8 bg-white mb-3 group-hover:w-16 transition-all duration-300"></div>
              <span class="text-white text-[18px] md:text-[22px] font-bold uppercase">Chauffeur Services</span>
            </div>
          </a>

          <!-- Small cards -->
          <div @click="router.push({name: 'CarRental'})" class="relative rounded-2xl overflow-hidden group cursor-pointer">
            <img src="/assets/10.jpeg" alt="Global Car Rental Destinations" class="w-full h-full object-cover aspect-[4/3]" />
            <div class="absolute inset-0 bg-gradient-to-t from-black/55 via-black/25 to-transparent"></div>
            <div class="absolute bottom-4 left-4">
              <div class="h-[2px] w-8 bg-white mb-3 group-hover:w-16 transition-all duration-300"></div>
              <span class="text-white text-[16px] md:text-[18px] font-bold uppercase">Car Rental</span>
            </div>
          </div>

          <div class="relative rounded-2xl overflow-hidden group cursor-pointer" @click="router.push({name: 'MeetGreet'})">
            <img src="/assets/4.jpg" alt="Professional Airport Meet and Greet Service" class="w-full h-full object-cover aspect-[4/3]" />
            <div class="absolute inset-0 bg-gradient-to-t from-black/55 via-black/25 to-transparent"></div>
            <div class="absolute bottom-4 left-4">
              <div class="h-[2px] w-8 bg-white mb-3 group-hover:w-16 transition-all duration-300"></div>
              <span class="text-white text-[16px] md:text-[18px] font-bold uppercase">Meet &amp; Greet</span>
            </div>
          </div>

          <div class="relative rounded-2xl overflow-hidden group cursor-pointer col-span-2 md:col-span-1" @click="router.push({name: 'BusesCoaches'})">
            <img src="/assets/busescoaches.jpeg" alt="Global Bus and Coach Charter for Groups" class="w-full h-full object-cover aspect-[4/3]" />
            <div class="absolute inset-0 bg-gradient-to-t from-black/55 via-black/25 to-transparent"></div>
            <div class="absolute bottom-4 left-4">
              <div class="h-[2px] w-8 bg-white mb-3 group-hover:w-16 transition-all duration-300"></div>
              <span class="text-white text-[16px] md:text-[18px] font-bold uppercase">Buses &amp; Coaches</span>
            </div>
          </div>

          <div class="relative rounded-2xl overflow-hidden group cursor-pointer col-span-2 md:col-span-1" @click="router.push({name: 'Planmytrip'})">
            <img src="/assets/5.jpg" alt="Corporate and Leisure Travel Planning" class="w-full h-full object-cover aspect-[4/3]" />
            <div class="absolute inset-0 bg-gradient-to-t from-black/55 via-black/25 to-transparent"></div>
            <div class="absolute bottom-4 left-4">
              <div class="h-[2px] w-8 bg-white mb-3 group-hover:w-16 transition-all duration-300"></div>
              <span class="text-white text-[14px] md:text-[16px] font-bold uppercase">Leisure &amp; Business Special Event</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- WHY CHOOSE US -->
    <section class="bg-white py-16 md:py-20">
      <div class="max-w-7xl mx-auto px-5 md:px-8 lg:px-10">
        <h2 class="text-center uppercase text-[#0B6A1F] font-semibold text-[14px] tracking-[0.18em]">Why choose us</h2>
        <div class="text-center text-slate-700 text-[22px] md:text-[34px] font-light mt-1">
          Proudly Serving the MENA Region Since 1988
        </div>

        <!-- desktop banner -->
        <div class="hidden md:flex justify-center mt-12">
          <img src="/assets/11.png" alt="Vehitra World" class="max-w-4xl w-full" />
        </div>

        <!-- mobile value list -->
        <div class="md:hidden mt-6">
          <p class="text-slate-600 text-[16px] font-semibold">
            7 CORE VALUES that make us the best transportation company serving the MENA travelers to more than 60,000 worldwide locations.
          </p>

          <div class="mt-6 space-y-5">
            <div v-for="(v, i) in values" :key="i" class="flex items-center gap-3">
              <div class="rounded-full border-2 border-slate-300 p-2">
                <i class="fa-solid fa-check text-[#0B6A1F]"></i>
              </div>
              <div class="text-slate-600 text-[14px] uppercase tracking-wide">{{ v }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- TESTIMONIALS + IMAGE -->
    <section class="bg-white">
      <div class="flex flex-col md:flex-row flex-wrap w-full">
        <div class="flex flex-1 bg-slate-50 py-14 md:py-20">
          <Swiper :spaceBetween="30" :pagination="{ clickable: true }" :modules="modules" class="my-swiper swipe-testimonials">
            <SwiperSlide v-for="(t, idx) in testimonials" :key="idx">
              <div class="flex flex-col items-center w-full mx-auto px-6 md:px-10 text-center">
                <div class="relative">
                  <div class="w-12 h-12 rounded-full bg-white shadow-sm"></div>
                  <span class="font-icon theme-icon comments-icon text-[#0B6A1F] absolute -top-3 -left-2 text-[46px]"></span>
                </div>
                <div class="mt-8 md:mt-10 text-slate-600 text-[16px] md:text-[18px] leading-relaxed font-light max-w-3xl">
                  {{ t.text }}
                </div>
                <div class="uppercase text-slate-500 text-[11px] tracking-[0.2em] mt-6">{{ t.name }}</div>
              </div>
            </SwiperSlide>
          </Swiper>
        </div>
        <div class="md:flex md:flex-1 bg-center bg-no-repeat bg-cover banner-cars h-[220px] md:h-auto"></div>
      </div>
    </section>

    <!-- CTA BANNER -->
    <section class="py-16 md:py-24">
      <div class="bg-center bg-no-repeat bg-cover banner-2-img rounded-2xl overflow-hidden">
        <div class="bg-white/80 backdrop-blur-sm md:bg-white/70 px-6 md:px-10 py-10 md:py-12 w-[92%] md:w-[420px] mx-auto md:ml-16 md:my-16 rounded-2xl shadow">
          <div class="flex flex-col items-center text-center">
            <span class="font-icon theme-icon tower-icon text-[#0B6A1F] text-[44px]"></span>
            <h4 class="uppercase text-slate-700 font-bold text-[18px] md:text-[22px] pt-3">Your Next Travel With Us</h4>
            <div class="text-slate-600 text-[16px] md:text-[18px] font-light pt-4">
              Use our online tool to request your preferred ride
            </div>
            <div class="mt-6 inline-flex items-center justify-center rounded-full px-6 py-3 border-2 border-[#0B6A1F] text-[#0B6A1F] text-[13px] uppercase tracking-wide hover:bg-[#0B6A1F] hover:text-white transition">
              Apply Now
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- KEY FEATURES -->
    <section class="pb-16 md:pb-24">
      <div class="max-w-7xl mx-auto px-5 md:px-8 lg:px-10">
        <h2 class="text-center uppercase text-[#0B6A1F] font-semibold text-[14px] tracking-[0.18em]">Key Features</h2>
        <div class="text-center text-slate-700 text-[22px] md:text-[34px] font-light mt-1">Make Your Trip Your Way With Us</div>

        <div class="mt-12 md:mt-16 grid md:grid-cols-4 gap-10 md:gap-8">
          <div class="flex flex-col items-center text-center p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition">
            <div class="relative">
              <div class="w-12 h-12 rounded-full bg-slate-100"></div>
              <span class="font-icon theme-icon car-hood-icon text-[#0B6A1F] absolute -top-2 -left-1 text-[40px]"></span>
            </div>
            <div class="text-slate-700 font-semibold mt-4">Safety First</div>
            <div class="text-slate-600 text-[14px] leading-relaxed mt-2">Experienced staff and professionally trained chauffeurs</div>
          </div>

          <div class="flex flex-col items-center text-center p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition">
            <div class="relative">
              <div class="w-12 h-12 rounded-full bg-slate-100"></div>
              <span class="font-icon theme-icon bus-icon text-[#0B6A1F] absolute -top-2 -left-1 text-[40px]"></span>
            </div>
            <div class="text-slate-700 font-semibold mt-4">Largest Fleet</div>
            <div class="text-slate-600 text-[14px] leading-relaxed mt-2">We offer you an extensive fleet including the latest models sedans, premium, and luxury.</div>
          </div>

          <div class="flex flex-col items-center text-center p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition">
            <div class="relative">
              <div class="w-12 h-12 rounded-full bg-slate-100"></div>
              <span class="font-icon theme-icon paper-plane-icon text-[#0B6A1F] absolute -top-2 -left-1 text-[40px]"></span>
            </div>
            <div class="text-slate-700 font-semibold mt-4">International Service</div>
            <div class="text-slate-600 text-[14px] leading-relaxed mt-2">We provide our transportation services in more than 60,000 worldwide locations.</div>
          </div>

          <div class="flex flex-col items-center text-center p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition">
            <div class="relative">
              <div class="w-12 h-12 rounded-full bg-slate-100"></div>
              <span class="font-icon theme-icon road-map-icon text-[#0B6A1F] absolute -top-2 -left-1 text-[40px]"></span>
            </div>
            <div class="text-slate-700 font-semibold mt-4">GCC Prime Service</div>
            <div class="text-slate-600 text-[14px] leading-relaxed mt-2">Services across all GCC Countries with latest model cars and professional drivers.</div>
          </div>
        </div>

        <div class="flex justify-center mt-10">
          <div class="inline-flex items-center justify-center rounded-full px-8 py-3 border-2 border-[#0B6A1F] text-[#0B6A1F] text-[12px] uppercase tracking-wide hover:bg-[#0B6A1F] hover:text-white transition">
            About Company
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router'
import { ref, watchEffect, computed } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Pagination } from 'swiper/modules'
import Search from '@/components/Search.vue'
import CouponModal from '@/components/CouponModal.vue'
import 'swiper/css'
import 'swiper/css/pagination'
import { retrieveUserTempSearch, deleteUserTempSearch } from '@/services/search-service'
const router = useRouter()
const modules = [Pagination]
const search = retrieveUserTempSearch()
deleteUserTempSearch()

/* === Robust mapping (aliases -> chip key -> legacy Search prop) === */
const OUTPUT_TYPES: Record<string, string> = {
  airport:        'airport_transfer',   
  hourly:         'full_day_chauffeur', 
  rental:         'car_rental',
  meet_greet:     'meet_greet',
  buses_coaches:  'buses_coaches',
  multi:          'multiple_requests',
}

const ALIAS_TO_CHIP: Record<string, string> = {
  // airport
  airport: 'airport',
  airport_transfer: 'airport',
  airporttransfer: 'airport',

  // chauffeur / hourly
  hourly: 'hourly',
  full_day: 'hourly',
  'full-day': 'hourly',
  full_day_chauffeur: 'hourly',
  chauffeur_full_day: 'hourly',
  chauffeur: 'hourly',

  // rental
  rental: 'rental',
  car_rental: 'rental',
  carrental: 'rental',

  // meet & greet
  meet_greet: 'meet_greet',
  meetandgreet: 'meet_greet',
  meet_and_greet: 'meet_greet',
  'meet-greet': 'meet_greet',

  // buses & coaches
  buses_coaches: 'buses_coaches',
  busesandcoaches: 'buses_coaches',
  buses_and_coaches: 'buses_coaches',
  busescoaches: 'buses_coaches',
  bus_coach: 'buses_coaches',

  // multiple requests
  multi: 'multi',
  multiple: 'multi',
  multiple_requests: 'multi',
  multiplerequests: 'multi'
}

const toChipKey = (val: unknown): string => {
  if (!val) return 'hourly'
  const str = String(val).toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
  return ALIAS_TO_CHIP[str] ?? 'hourly'
}

const services = [
  { label: 'Airport Transfer',   type: 'airport' },
  { label: 'Full Day Chauffeur', type: 'hourly' },
  { label: 'Car Rental',         type: 'rental' },
  { label: 'Meet & Greet',       type: 'meet_greet' },
  { label: 'Buses & Coaches',    type: 'buses_coaches' },
  { label: 'Multiple Requests',  type: 'multi' },
]

const initialTypeRaw =
  (search && (search as any).driveType) ||
  (search && (search as any).type) ||
  'hourly'

const activeType = ref<string>(toChipKey(initialTypeRaw))

watchEffect(() => {
  const known = services.map(s => s.type)
  if (!known.includes(activeType.value)) activeType.value = 'hourly'
})

const mappedType = computed(() => OUTPUT_TYPES[activeType.value] ?? 'hourly')
/* === /mapping === */

const testimonials = [
  { name: 'Aisha Rawan', text: 'I couldn’t be happier with the transfer you did. It was great and the car was amazing. Thank you for your great service and I will continue to refer friends and family to you all.' },
  { name: 'Muhammad', text: 'Great experience with Vehitra World. I have used many others in Saudi Arabia but this was by far the best. Great customer service, wonderful drivers and by far the best and most affordable company I have used.' },
  { name: 'Kevin Foster', text: 'I hired Vehitra World to pick me up at the airport with their Sedan… The driver was on time and extremely courteous. Great service!' },
  { name: 'Mitch Michael', text: 'This is the best service I have had so far. The driver was punctual and very helpful; he knew the city well.' }
]


// Which service types use the inline Search form on this page
const SEARCH_TYPES = new Set(['airport', 'hourly'])

// External destinations by type (open in new tab)
const EXTERNAL_LINKS: Record<string, string> = {
   about_us: 'https://vehitraworld.com/pages/About-Us',
}

// Internal routes by type
const ROUTE_BY_TYPE: Record<string, string> = {
  meet_greet: 'MeetGreet',
  buses_coaches: 'BusesCoaches',
  multi: 'Planmytrip', // change if your route name differs
}

// Smooth scroll helper
const smoothScrollTo = (id: string) => {
  if (typeof window === 'undefined') return
  const el = document.getElementById(id)
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

// Chips: decide what to do for each service
const handleServiceSelect = (type: string) => {
  if (SEARCH_TYPES.has(type)) {
    activeType.value = type
    // bring user to the form on mobile where chips live above it
    smoothScrollTo('mainbanner')
    return
  }
  if (type === 'rental') {
      router.push({ name: 'CarRental' })
      return
  }
  if (EXTERNAL_LINKS[type]) {
    window.open(EXTERNAL_LINKS[type], '_blank')
    return
  }
  if (ROUTE_BY_TYPE[type]) {
    router.push({ name: ROUTE_BY_TYPE[type] })
    return
  }
  // fallback: keep Search usable
  activeType.value = 'hourly'
}

// “What we offer” big cards (Airport/Chauffeur)
const selectSearchAndScroll = (type: string) => {
  activeType.value = type
  smoothScrollTo('mainbanner')
}
const values = [
  'FIRST RATE CUSTOMER SERVICE',
  'CLEAN AND WELL-MAINTAINED VEHICLES',
  'INNOVATIVE USE OF TECHNOLOGY',
  'PUNCTUALITY AND VERACITY',
  'PURSUIT OF CONTINUOUS IMPROVEMENT',
  'SAFETY IS OUR HIGHEST PRIORITY',
  'HIGHLY TRAINED PROFESSIONAL DRIVERS'
]
</script>

<style scoped>
/* Hide scrollbar for chip row */
.no-scrollbar::-webkit-scrollbar { display: none; }
.no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

/* If your Search.vue renders its own tab list, you can hide it on mobile:
:deep(.search-tabs){ display:none; }
@media (min-width:768px){ :deep(.search-tabs){ display:flex; } }
*/
</style>